/**
 * Análise do período — a visão de conjunto que a fila não dá.
 *
 * A fila responde "o que eu trato agora"; isto responde "de onde isso vem, e
 * qual anúncio produz". São as agregações do painel original, sobre o recorte
 * de período que o usuário escolher.
 */

import { useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartEmpty } from '../../components/ChartEmpty'
import {
  NATUREZA_LABEL,
  TIPO_CURTO,
  agregarPorItem,
  brl0,
  diaCurto,
  matrizAnuncioTipo,
  motivosAgrupados,
  motivosShopee,
  solucoesAplicadas,
  type CasoMl,
  type DadosShopee,
  type FiltroLojaShopee,
  type Natureza,
} from './logic'

const CORES_NATUREZA = (t: Theme): Record<Natureza, string> => ({
  g1: t.palette.error.main,
  g2: t.palette.info.main,
  g3: t.palette.warning.main,
  g4: t.palette.text.disabled,
})

/** Reclamações por dia no recorte — mostra pico de lote, que média esconde. */
function serieDiaria(casos: CasoMl[]): Array<{ dia: string; casos: number }> {
  const porDia = new Map<string, number>()
  for (const caso of casos) {
    if (!caso.data) continue
    porDia.set(caso.data, (porDia.get(caso.data) || 0) + 1)
  }
  return [...porDia.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([data, n]) => ({ dia: diaCurto(data), casos: n }))
}

// ---------------------------------------------------------------- Mercado Livre

export function AnaliseMl({ casos }: { casos: CasoMl[] }) {
  const theme = useTheme()
  const cores = CORES_NATUREZA(theme)

  const barras = useMemo(() => motivosAgrupados(casos), [casos])
  const { ranking, familias } = useMemo(() => agregarPorItem(casos), [casos])
  const matriz = useMemo(() => matrizAnuncioTipo(ranking, 12), [ranking])
  const serie = useMemo(() => serieDiaria(casos), [casos])
  const solucoes = useMemo(() => solucoesAplicadas(casos), [casos])

  if (!casos.length) {
    return <ChartEmpty title="Nenhuma reclamação no período escolhido." suggestPeriod={false} />
  }

  const maxBarra = barras[0]?.n || 1
  const maxFamilia = familias[0]?.n || 1

  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Typography variant="h6">Reclamações por dia</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {casos.length} casos no recorte. Pico concentrado costuma ser lote de expedição, não
            piora gradual — vale cruzar com a data do pedido antes de tratar como tendência.
          </Typography>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serie}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="dia" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="casos" name="reclamações" fill={theme.chart.line} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6">De onde vêm as reclamações</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Agrupado pelo motivo que o comprador escolheu. Barra vermelha = motivo que o ML
                trata como <b>válido e não excluível</b> — não existe texto que reverta.
              </Typography>
              <Stack spacing={1}>
                {barras.map((b) => (
                  <Box key={b.label}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {b.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {b.n} · {b.pct}%
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 7, borderRadius: 999, bgcolor: 'action.hover', mt: 0.5 }}>
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: 999,
                          width: `${Math.max(2, (b.n / maxBarra) * 100)}%`,
                          bgcolor: b.hot ? 'error.main' : 'primary.main',
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6">Onde a reclamação nasce</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Famílias de produto, pela palavra que manda no título. A barra é dividida pela
                natureza do caso.
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {(Object.keys(NATUREZA_LABEL) as Natureza[]).map((g) => (
                  <Stack key={g} direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 11, height: 11, borderRadius: 0.5, bgcolor: cores[g] }} />
                    <Typography variant="caption" color="text.secondary">
                      {NATUREZA_LABEL[g]}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack spacing={1.25}>
                {familias.map((f) => (
                  <Box key={f.nome}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2">
                        <b>{f.nome}</b>{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          · {f.anuncios} anúncio{f.anuncios > 1 ? 's' : ''} · {brl0(f.valor / f.n)}/caso
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {f.n}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      sx={{
                        height: 7,
                        borderRadius: 999,
                        overflow: 'hidden',
                        mt: 0.5,
                        width: `${Math.max(4, (f.n / maxFamilia) * 100)}%`,
                      }}
                    >
                      {(Object.keys(NATUREZA_LABEL) as Natureza[]).map((g) =>
                        f.natureza[g] ? (
                          <Box key={g} sx={{ width: `${(f.natureza[g] / f.n) * 100}%`, bgcolor: cores[g] }} />
                        ) : null,
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6">Soluções aplicadas</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {solucoes.aplicadas.length
              ? `${solucoes.total - solucoes.semDesfecho} de ${solucoes.total} casos tiveram desfecho registrado. Caso que morreu sozinho não entra aqui — não foi solução da equipe.`
              : 'Nenhum desfecho registrado neste recorte.'}
          </Typography>
          {solucoes.aplicadas.length ? (
            <Stack spacing={1}>
              {solucoes.aplicadas.map((s) => (
                <Box key={s.label}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {s.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {s.n} · {s.pct}% · {brl0(s.valor)}
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 7, borderRadius: 999, bgcolor: 'action.hover', mt: 0.5 }}>
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: 999,
                        width: `${Math.max(2, s.pct)}%`,
                        bgcolor: theme.chart.line,
                      }}
                    />
                  </Box>
                </Box>
              ))}
              {solucoes.semDesfecho ? (
                <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                  {solucoes.semDesfecho} caso{solucoes.semDesfecho === 1 ? '' : 's'} sem desfecho
                  registrado — ainda em aberto, ou encerrado sem resolução na API.
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              O desfecho só vem do histórico gravado. Se esta aba estiver lendo o arquivo da leitura
              manual, este quadro fica vazio — o campo não existia naquela coleta.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6">Anúncios com mais reclamações</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Ordenado por número de casos. O valor é a soma dos pedidos reclamados. Sem o total de
            vendas de cada anúncio, leia como <b>onde o problema aparece</b>, não como "qual anúncio
            é pior".
          </Typography>
          <Box sx={{ overflowX: 'auto', maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Anúncio</TableCell>
                  <TableCell>Conta</TableCell>
                  <TableCell align="right">Rec.</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Motivo que manda</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranking.slice(0, 40).map((o) => {
                  const dominante = Object.entries(o.tipos).sort((a, b) => b[1] - a[1])[0]
                  return (
                    <TableRow key={o.mlb}>
                      <TableCell sx={{ minWidth: 240 }}>
                        <Typography variant="body2">{o.titulo}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.mlb}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {o.contas.F ? <Chip size="small" variant="outlined" label="FESTA" sx={{ mr: 0.5 }} /> : null}
                        {o.contas.H ? <Chip size="small" variant="outlined" label="HOME" /> : null}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {o.n}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {brl0(o.valor)}
                      </TableCell>
                      <TableCell>
                        {dominante ? (
                          <>
                            {dominante[0]}{' '}
                            <Box component="span" sx={{ color: 'text.secondary' }}>
                              {dominante[1]}
                            </Box>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {matriz.linhas.length ? (
        <Card>
          <CardContent>
            <Typography variant="h6">Anúncio × tipo de reclamação</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Os 12 anúncios mais reclamados do recorte, e em que tipo cada um cai. Célula mais
              escura = mais casos.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Anúncio</TableCell>
                    {matriz.colunas.map((c) => (
                      <TableCell key={c} align="center" sx={{ fontSize: 11, lineHeight: 1.2 }}>
                        {TIPO_CURTO[c] || c}
                      </TableCell>
                    ))}
                    <TableCell align="center">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matriz.linhas.map(({ item, celulas, total }) => (
                    <TableRow key={item.mlb}>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">
                          {item.titulo.length > 46 ? `${item.titulo.slice(0, 46)}…` : item.titulo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.mlb}
                        </Typography>
                      </TableCell>
                      {celulas.map((n, i) => (
                        <TableCell
                          key={i}
                          align="center"
                          sx={{
                            color: n ? undefined : 'text.disabled',
                            bgcolor: n
                              ? alpha(theme.palette.error.main, 0.14 + 0.6 * (n / matriz.max))
                              : undefined,
                            fontWeight: n / matriz.max > 0.6 ? 700 : undefined,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {n || '·'}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {total}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  )
}

// ---------------------------------------------------------------- Shopee

export function AnaliseShopee({
  dados,
  loja,
  onLoja,
}: {
  dados: DadosShopee
  loja: FiltroLojaShopee
  onLoja: (v: FiltroLojaShopee) => void
}) {
  const theme = useTheme()
  const barras = useMemo(() => motivosShopee(dados.motivos, loja), [dados.motivos, loja])
  const maxBarra = barras[0]?.total || 1
  const serie = dados.serieDiaria || []

  return (
    <Stack spacing={2.5}>
      {serie.length ? (
        <Card>
          <CardContent>
            <Typography variant="h6">Devoluções abertas por dia</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              As duas lojas, no período da leitura.
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} interval={2} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="festa" name="FESTA" stackId="s" fill={theme.chart.line} />
                  <Bar
                    dataKey="decora"
                    name="DECORA"
                    stackId="s"
                    fill={theme.chart.pie[5] || theme.chart.fill}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <ChartEmpty
          title="Série diária indisponível nesta fonte."
          hint="O gráfico por dia vem da leitura em arquivo; o endpoint devolve os casos, não a série agregada."
          suggestPeriod={false}
          dense
        />
      )}

      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ mr: 1 }}>
              Motivos
            </Typography>
            {(
              [
                ['all', 'As duas lojas'],
                ['L1', 'FESTA'],
                ['L2', 'DECORA'],
              ] as Array<[FiltroLojaShopee, string]>
            ).map(([v, label]) => (
              <Chip
                key={v}
                size="small"
                label={label}
                color={loja === v ? 'primary' : 'default'}
                variant={loja === v ? 'filled' : 'outlined'}
                onClick={() => onLoja(v)}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            A parte clara da barra é o que foi cancelado sozinho, sem ação da equipe.
          </Typography>
          <Stack spacing={1.25}>
            {barras.map((m) => (
              <Box key={m.cod}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2">
                    <b>{m.label}</b>{' '}
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>
                      {m.cod}
                    </Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {m.total} · {brl0(m.valor)}
                  </Typography>
                </Stack>
                <Stack direction="row" sx={{ height: 16, borderRadius: 0.5, overflow: 'hidden', mt: 0.5 }}>
                  <Box sx={{ width: `${(m.mantidas / maxBarra) * 100}%`, bgcolor: theme.chart.line }} />
                  <Box
                    sx={{
                      width: `${(m.canceladas / maxBarra) * 100}%`,
                      bgcolor: alpha(theme.palette.text.disabled, 0.45),
                    }}
                  />
                </Stack>
                {m.canceladas ? (
                  <Typography variant="caption" color="text.secondary">
                    {m.canceladas} canceladas sozinhas ({m.pctCanceladas}%)
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}
